/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Pribyl (pribyl@cadwork.cz)
 *
 */

export type Txy = {x: number, y: number};
export type Tll =  {latitude: number, longitude: number};

export default
class WGS84Projection {
    private readonly MinLatitude: number = -85.05112878;
    private readonly MaxLatitude: number = 85.05112878;
    private readonly MinLongitude: number = -180;
    private readonly MaxLongitude: number = 180;

    constructor() {
    }


/// <summary>
/// Determines the map width and height (in pixels) at a specified level
/// of detail.
/// </summary>
/// <param name="levelOfDetail">Level of detail, from 1 (lowest detail)
/// to 23 (highest detail).</param>
/// <returns>The map width and height in pixels.</returns>
    private _getMapSize(aLevelOfDetail: number): number {
        return 256 << aLevelOfDetail;
    }

/// <summary>
/// Clips a number to the specified minimum and maximum values.
/// </summary>
/// <param name="n">The number to clip.</param>
/// <param name="minValue">Minimum allowable value.</param>
/// <param name="maxValue">Maximum allowable value.</param>
/// <returns>The clipped value.</returns>
    private _clip(aN: number, aMinValue: number, aMaxValue: number): number {
        return Math.min(Math.max(aN, aMinValue), aMaxValue);
    }

// based on: https://msdn.microsoft.com/en-us/library/bb259689.aspx
/// <summary>
/// Converts a point from latitude/longitude WGS-84 coordinates (in degrees)
/// into pixel XY coordinates at a specified level of detail.
/// </summary>
/// <param name="latitude">Latitude of the point, in degrees.</param>
/// <param name="longitude">Longitude of the point, in degrees.</param>
/// <param name="levelOfDetail">Level of detail, from 1 (lowest detail)
/// to 23 (highest detail).</param>
/// <param name="pixel">Output parameter receiving the x and y coordinate in pixels.</param>
    private _lngLatToPixelXY(aLongitude: number, aLatitude: number, aLevelOfDetail: number, aRoundResult: boolean): Txy {
        let latitude = this._clip(aLatitude, this.MinLatitude, this.MaxLatitude);
        let longitude = this._clip(aLongitude, this.MinLongitude, this.MaxLongitude);

        let x = (longitude + 180) / 360;
        let sinLatitude = Math.sin(latitude * Math.PI / 180);
        let y = 0.5 - Math.log((1 + sinLatitude) / (1 - sinLatitude)) / (4 * Math.PI);

        let mapSize = this._getMapSize(aLevelOfDetail);

        let pixelXY = {
            x: this._clip(x * mapSize + 0.5, 0, mapSize - 1),
            y: this._clip(y * mapSize + 0.5, 0, mapSize - 1)
        };

        if (aRoundResult) {
            return {
                x: Math.round(pixelXY.x),
                y: Math.round(pixelXY.y)
            }
        } else {
            return pixelXY;
        }
    }

    public lngLatToTileXY(aLongitude: number, aLatitude: number, aLevel: number, aRoundResult: boolean): Txy {
        let pixel = this._lngLatToPixelXY(aLongitude, aLatitude, aLevel, aRoundResult);
        let tile = this._pixelXYToTileXY(pixel);
        return tile;
    }

    public tileXYToLngLat(aTile: Txy, aLevel: number): Tll {
        let pixel = this._tileXYToPixelXY({x: aTile.x, y: aTile.y});
        return this._pixelXYToLngLat(pixel, aLevel);
    }

/// <summary>
/// Converts a pixel from pixel XY coordinates at a specified level of detail
/// into latitude/longitude WGS-84 coordinates (in degrees).
/// </summary>
/// <param name="pixelX">x and y coordinate of the point, in pixels.</param>
/// <param name="levelOfDetail">Level of detail, from 1 (lowest detail)
/// to 23 (highest detail).</param>
/// <param name="latlng">Output parameter receiving the latitude and longitude in degrees.</param>
    private _pixelXYToLngLat(aPixel: Txy, aLevelOfDetail: number): Tll {
        let mapSize = this._getMapSize(aLevelOfDetail);
        let x = (this._clip(aPixel.x, 0, mapSize - 1) / mapSize) - 0.5;
        let y = 0.5 - (this._clip(aPixel.y, 0, mapSize - 1) / mapSize);

        return {
            latitude: 90 - 360 * Math.atan(Math.exp(-y * 2 * Math.PI)) / Math.PI,
            longitude: 360 * x
        }
    }

/// <summary>
/// Converts pixel XY coordinates into tile XY coordinates of the tile containing
/// the specified pixel.
/// </summary>
/// <param name="pixelX">Pixel X coordinate.</param>
/// <param name="pixelY">Pixel Y coordinate.</param>
/// <param name="tileX">Output parameter receiving the tile X coordinate.</param>
/// <param name="tileY">Output parameter receiving the tile Y coordinate.</param>
    private _pixelXYToTileXY(aPixel: Txy): Txy {
        return {
            x: aPixel.x / 256,
            y: aPixel.y / 256
        }
    }

/// <summary>
/// Converts tile XY coordinates into pixel XY coordinates of the upper-left pixel
/// of the specified tile.
/// </summary>
/// <param name="tile">Tile x and y coordinate.</param>
/// <param name="pixel">Output parameter receiving the pixel x and y coordinate.</param>
    private _tileXYToPixelXY(aTile: Txy): Txy {
        return {
            x: aTile.x * 256,
            y: aTile.y * 256
        }
    }
}

